import React, {
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from 'react';
import { RootState } from 'reducer';
import { useDispatch, useSelector } from 'react-redux';
import { useSelectGroupById } from 'entity/group/groupHooks';
import { updateGroupUnreadRequest } from 'entity/group/groupRedux';
import {
  getSeedlyEventTitle,
  isSeedlyEventSlug,
} from 'entity/group/groupUtils';
import { usePostCurrentPage, usePostTotalCount } from 'entity/post/postHooks';
import { getGroupPostsRequest } from 'entity/post/postRedux';
import { resetPostComment } from 'entity/comment/commentRedux';
import useIsMobile from 'utils/hooks/useIsMobile';

import useSeedlyEventTopic from 'components/post/CreatePostForm/hooks/useSeedlyEventTopic';
import PostList from 'components/post/PostList';
import { useRouter } from 'next/router';
import { ArrowLeft } from 'react-feather';
import { PageContext } from 'context';
import get from 'lodash/get';
import clsx from 'clsx';

import useStickyHeader from '../../hooks/useStickyHeader';
import GroupJumbotron from './GroupJumbotron';
import AboutCard from './AboutCard';
import FilterBarWrapper from '../FilterBarWrapper';

import { CommunityPageContext } from '../../context/CommunityPageContext';
import useScrollToComponent from '../../hooks/useScrollToComponent';
import MockDiscussionCard from '../HomeContent/MockDiscussionCard';
import EpisodeCard from './EpisodeCard';

type GroupFilterType = 'recent' | 'new' | 'trending' | 'about' | 'event';

const getGroupSortByParam = (groupFilter: GroupFilterType) => {
  if (groupFilter === 'recent') return { sort_by_recents: true };
  if (groupFilter === 'new') return { sort_by_new: true };
  if (groupFilter === 'trending') return { sort_by_trending: true };
  return { sort_by_recents: true };
};

const GroupContent = () => {
  const { groupId } = useContext(CommunityPageContext);
  const { showHeader } = useContext(PageContext);
  const filterBarRef = useRef(null);
  const router = useRouter();
  const { query } = router;
  const seedlyEventSlug = get(query, 'filter', '').toString();

  const dispatch = useDispatch();

  // Select group from redux to get following group status.
  const group = useSelectGroupById(groupId);
  const isMobile = useIsMobile();
  const isSticky = useStickyHeader(filterBarRef);
  const scrollToFilterBar = useScrollToComponent(filterBarRef);
  const currentPage = usePostCurrentPage();
  const totalCount = usePostTotalCount();
  const profile = useSelector((state: RootState) => state.profile.user);

  const [selected, setSelected] = useState<GroupFilterType>('recent');
  const seedlyEventTopics = useSeedlyEventTopic(group);
  const currentEventTopic = useMemo(() => {
    return seedlyEventTopics.find(topic => {
      return topic.slug === seedlyEventSlug;
    });
  }, [seedlyEventTopics, seedlyEventSlug]);

  let showMockCard = true;
  if (group?.slug === 'announcements' && !profile?.officialAccount) {
    showMockCard = false;
  }
  // Update group post unread count
  useEffect(() => {
    if (group && group.unreadCount > 0) {
      dispatch(updateGroupUnreadRequest(groupId));
    }
  }, [groupId, group]);

  // Reset filter to most recent when navigate to different group
  useEffect(() => {
    setSelected('recent');
  }, [groupId]);

  // Get group post when load on group page
  useEffect(() => {
    dispatch(
      getGroupPostsRequest({
        ...(seedlyEventSlug && { seedlyevent_slug: seedlyEventSlug }),
        group_id: groupId,
        sort_by_recents: true,
        page: 1,
      }),
    );
  }, [dispatch, groupId, seedlyEventSlug]);

  // Get group post when navigating on different filter
  const handleFilterSelect = useCallback(
    filterItem => {
      setSelected(filterItem.value);
      if (filterItem.value !== 'about') {
        dispatch(resetPostComment());
        dispatch(
          getGroupPostsRequest({
            ...getGroupSortByParam(filterItem.value),
            ...(seedlyEventSlug && { seedlyevent_slug: seedlyEventSlug }),
            group_id: groupId,
            page: 1,
          }),
        );
      }
    },
    [dispatch, groupId, seedlyEventSlug],
  );

  // Handling Loading more groups
  const handleLoadMorePost = useCallback(() => {
    dispatch(
      getGroupPostsRequest({
        ...getGroupSortByParam(selected),
        group_id: groupId,
        page: currentPage + 1,
      }),
    );
  }, [dispatch, groupId, selected, currentPage]);

  const filterList = useMemo(() => {
    const list: {
      title: string;
      value: GroupFilterType;
    }[] = [
      {
        title: 'Recent',
        value: 'recent',
      },
      {
        title: 'New',
        value: 'new',
      },
      {
        title: 'Trending',
        value: 'trending',
      },
      {
        title: 'About',
        value: 'about',
      },
    ];

    if (isSeedlyEventSlug(group?.slug)) {
      list.push({
        title: getSeedlyEventTitle(group?.slug),
        value: 'event',
      });
    }

    return list;
  }, [group]);

  const renderByFilter = select => {
    switch (select) {
      case 'about':
        return <AboutCard content={group.about} />;
      case 'event':
        return (
          <EpisodeCard
            onEpisodeClick={() => setSelected('recent')}
            seedlyEventTopics={seedlyEventTopics}
            group={group}
          />
        );
      default:
        return (
          <>
            <PostList handleLoadMorePost={handleLoadMorePost} />
          </>
        );
    }
  };

  return (
    <div className="flex flex-col gap-1 w-full items-center	">
      {group && <GroupJumbotron group={group} />}
      <div ref={filterBarRef} />
      <div className="flex flex-col w-full items-center	">
        {currentEventTopic && (
          <div className="card mt-2">
            <div className="flex gap-4 items-center">
              <ArrowLeft
                className="cursor-pointer"
                onClick={() => router.push(`/community/${group.slug}`)}
                size={24}
              />
              <div className="flex flex-col ">
                <p className="text-base font-bold">{currentEventTopic.name}</p>
                <p className="text-neutral-500">
                  {totalCount} Post{totalCount > 1 && 's'}
                </p>
              </div>
            </div>
          </div>
        )}
        {showMockCard && (
          <div className="mt-2 w-full">
            <MockDiscussionCard />
          </div>
        )}
        <div className={clsx('mt-2 w-full', isSticky && 'mt-0')}>
          <FilterBarWrapper
            isSticky={isSticky}
            withHeader={isSticky && showHeader}
          >
            <div className="flex gap-5">
              {filterList.map(filter => {
                const isSelected = filter.value === selected;
                return (
                  <span
                    key={filter.title}
                    className={clsx(
                      'text-sm font-bold cursor-pointer',
                      isSelected ? 'text-blue-500' : 'text-neutral-500',
                    )}
                    onClick={() => {
                      handleFilterSelect(filter);
                      if (isMobile) {
                        scrollToFilterBar();
                      }
                    }}
                    data-testid={`${filter.value}-filter-button`}
                  >
                    {filter.title}
                  </span>
                );
              })}
            </div>
          </FilterBarWrapper>
        </div>
        <div className="mt-2 w-full">{renderByFilter(selected)}</div>
      </div>
    </div>
  );
};

export default GroupContent;
